/**
 * The one place this app decides where the admin API is.
 *
 * The dashboard reads `VITE_ADMIN_API_URL` and nothing else: two hostnames exist so that the session
 * credential Task 12 issues is host-scoped to the authenticated one, and a dashboard that reached for
 * the public origin would be the first step back toward one host for both.
 */
import { resolveApiBaseUrl } from './base-url.ts';
import { createApiClient, type ApiClient } from './client.ts';

export const adminApiUrlVariable = 'VITE_ADMIN_API_URL';

/** The slice of `import.meta.env` this app reads. */
export interface AdminApiEnv {
  readonly VITE_ADMIN_API_URL?: string | undefined;
  /** Vite's own flag: true for `vite dev`, false for a production build. */
  readonly DEV: boolean;
}

/**
 * The one place in this repository that turns credentials on.
 *
 * `credentials: 'include'` is what makes the browser attach the `apr_session` cookie the admin API
 * issued, and it is here — in the dashboard's own, not-shared factory — rather than in `client.ts`,
 * which is byte-identical to the site's copy. The site's `public-api.ts` leaves it off, and
 * `apps/web/src/api/public-api.test.ts` asserts that it stays off: requirement 5.3 is that the public
 * site never carries a staff credential, and a shared default would be one edit away from breaking it
 * in both apps at once.
 *
 * Nothing here can read the credential. It is `httpOnly`, so `fetch` sends it and script cannot see
 * it, which is the whole of requirement 5.2.
 */
export const resolveAdminApiBaseUrl = (env: AdminApiEnv): string =>
  resolveApiBaseUrl({
    variableName: adminApiUrlVariable,
    value: env.VITE_ADMIN_API_URL,
    isDevelopment: env.DEV,
  });

export const createAdminApiClient = (env: AdminApiEnv): ApiClient =>
  createApiClient({
    baseUrl: resolveAdminApiBaseUrl(env),
    credentials: true,
  });
